import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useASINSalesHistory } from '@/context/ASINSalesHistoryContext';
import { Card, CardContent } from '@/components/ui/card';
import { AlertCircle } from 'lucide-react';
import { FaChartLine } from 'react-icons/fa';
import { OverallQuickInsights } from '@/components/asin-sales-history/OverallQuickInsights';
import { OverallPerformanceCharts } from '@/components/asin-sales-history/OverallPerformanceCharts';
import { DateRangeSelector } from '@/components/asin-sales-history/DateRangeSelector';
import { LoadingIndicator } from '@/components/asin-sales-history/LoadingIndicator';
import { OverallSalesHistoryMetrics, DateRangeValue, OverallSalesHistoryRow } from '@/types/asinSalesHistory';
import { asinSalesHistoryService } from '@/services/asinSalesHistoryService';
import { subDays, startOfDay } from 'date-fns';

// Helper function to normalize dates for consistent comparison
const normalizeDate = (date: Date): Date => {
  return startOfDay(new Date(date.getFullYear(), date.getMonth(), date.getDate()));
};

// Helper function to parse US date format (MM/DD/YYYY) consistently
const parseUSDate = (dateStr: string): Date | null => {
  try {
    // Handle various formats that might come from Google Sheets
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return null;
    return normalizeDate(date);
  } catch {
    return null;
  }
};

const OverallSalesHistory: React.FC = () => {
  // Use ASIN Sales History context for company selection
  const { selectedCompany } = useASINSalesHistory();
  
  // Local state for Overall Sales History data
  const [rawData, setRawData] = useState<OverallSalesHistoryRow[]>([]);
  const [companyName, setCompanyName] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'daily' | 'cumulative'>('daily');
  const [initialLoad, setInitialLoad] = useState(false); // Start as false - no loading on first URL load

  // Local state for date range and performance filters - matching Dashboard exactly
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    preset: 'last_30_days',
    startDate: subDays(new Date(), 30),
    endDate: new Date()
  });
  const [performanceFilter, setPerformanceFilter] = useState<'top_10' | 'bottom_10'>('top_10');
  const [filterType, setFilterType] = useState<'date' | 'overall'>('overall');

  // Load Overall Sales History data when company is selected
  useEffect(() => {
    if (!selectedCompany) {
      console.log('[OverallSalesHistory] No company selected, clearing data');
      setRawData([]);
      setCompanyName('');
      setError(null);
      return;
    }

    const loadOverallData = async () => {
      console.log('[OverallSalesHistory] Loading data for company:', selectedCompany);
      
      setLoading(true);
      setLoadingProgress(0);
      setError(null);
      setInitialLoad(true);
      
      try {
        // Simulate realistic loading progress
        const progressInterval = setInterval(() => {
          setLoadingProgress(prev => {
            if (prev < 80) return prev + Math.random() * 20 + 5;
            return prev;
          });
        }, 200);

        // Fetch data from "2. Overall Sales History" tab for selected company
        console.log('[OverallSalesHistory] Fetching data from Overall Sales History tab for company:', selectedCompany);
        setLoadingProgress(20);
        
        const overallResponse = await asinSalesHistoryService.getOverallSalesHistoryData(selectedCompany);
        
        setLoadingProgress(50);
        console.log('[OverallSalesHistory] Overall data received:', overallResponse.rows.length, 'rows');
        console.log('[OverallSalesHistory] Sample overall row:', overallResponse.rows[0]);
        console.log('[OverallSalesHistory] Company name:', overallResponse.client);
        
        // Store the company name from response
        setCompanyName(overallResponse.client || selectedCompany);
        
        setLoadingProgress(80);
        
        // Convert to OverallSalesHistoryRow format
        const formattedData = overallResponse.rows.map((row, index): OverallSalesHistoryRow => {
          const [date, traffic, conversionRate, units, revenue, ppcSpend, grossProfit] = row;
          
          const formattedRow = {
            id: `overall_${selectedCompany}_${date}`,
            company: selectedCompany,
            date: new Date(date),
            traffic: parseInt(traffic) || 0,
            conversionRate: parseFloat(conversionRate) || 0,
            units: parseInt(units) || 0,
            revenue: parseFloat(revenue) || 0,
            ppcSpend: parseFloat(ppcSpend) || 0,
            grossProfit: parseFloat(grossProfit) || 0,
            syncedAt: new Date(),
            sourceRow: index + 2
          };
          
          if (index === 0) {
            console.log('[OverallSalesHistory] First formatted overall row:', formattedRow);
          }
          
          return formattedRow;
        });
        
        console.log(`[OverallSalesHistory] Formatted ${formattedData.length} overall rows from "2. Overall Sales History" tab`);
        
        // Sort by date (most recent first)
        formattedData.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
        
        setLoadingProgress(95);
        console.log('[OverallSalesHistory] Formatted', formattedData.length, 'overall rows from sheet');
        
        // Complete loading
        clearInterval(progressInterval);
        setLoadingProgress(100);
        
        // Short delay to show 100% completion
        setTimeout(() => {
          setRawData(formattedData);
          setInitialLoad(false);
        }, 300);
        
      } catch (err) {
        console.error('[OverallSalesHistory] Error loading data:', err);
        console.error('[OverallSalesHistory] Error details:', {
          message: err instanceof Error ? err.message : 'Unknown error',
          stack: err instanceof Error ? err.stack : undefined,
          name: err instanceof Error ? err.name : 'Unknown',
          type: typeof err
        });
        
        // Log network details if it's a fetch error
        if (err instanceof Error && err.message.includes('fetch')) {
          console.error('[OverallSalesHistory] Network error detected. Browser details:', {
            userAgent: navigator.userAgent,
            language: navigator.language,
            online: navigator.onLine,
            cookiesEnabled: navigator.cookieEnabled
          });
        }
        
        // Log more detailed error message
        let errorMessage = 'Failed to load overall data';
        if (err instanceof Error) {
          errorMessage = `${err.name}: ${err.message}`;
          
          // Special handling for common errors
          if (err.message.includes('502')) {
            errorMessage = 'Server error (502): The Netlify function failed to respond. This could be due to environment variables not being set correctly or the function timing out.';
            console.error('[OverallSalesHistory] 502 Error detected. This is likely a Netlify function configuration issue.');
          } else if (err.message.includes('404')) {
            errorMessage = 'Not found (404): The requested resource was not found. Check that the function is deployed correctly.';
          } else if (err.message.includes('timeout') || err.message.includes('timed out')) {
            errorMessage = 'Timeout: The request took too long to complete. This might be due to large data processing or network issues.';
          }
        }
        
        console.error(`[OverallSalesHistory] Final error message: ${errorMessage}`);
        setError(errorMessage);
        setRawData([]);
        setInitialLoad(false);
      } finally {
        setTimeout(() => {
          setLoading(false);
          setLoadingProgress(0);
        }, 300);
      }
    };

    loadOverallData();
  }, [selectedCompany]); // Reload when company changes

  // Memoized date range calculation to prevent flicker
  const dateRangeKey = useMemo(() => {
    return `${dateRange.preset}_${dateRange.startDate?.getTime()}_${dateRange.endDate?.getTime()}`;
  }, [dateRange]);

  // Single source of truth for filtered data - always start from raw data
  const filteredData = useMemo(() => {
    console.log('[OverallSalesHistory] Filtering data. Raw data length:', rawData.length);
    console.log('[OverallSalesHistory] Date range:', dateRange);
    console.log('[OverallSalesHistory] View mode:', viewMode);
    
    if (!rawData.length) return [];
    
    // Apply date filter locally (deterministic, based on date range only)
    if (!dateRange.startDate || !dateRange.endDate) return rawData;
    
    const startDate = normalizeDate(dateRange.startDate);
    const endDate = normalizeDate(dateRange.endDate);
    
    const filtered = rawData.filter(row => {
      // row.date is already a Date object, just normalize it
      const rowDate = normalizeDate(row.date);
      
      // Inclusive range check: [startDate, endDate]
      return rowDate >= startDate && rowDate <= endDate;
    });

    console.log('[OverallSalesHistory] Filtered data length:', filtered.length);
    
    // Apply view mode transformation
    if (viewMode === 'cumulative') {
      // Calculate cumulative totals
      let cumulativeData = [...filtered].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
      let runningTotals = {
        traffic: 0,
        units: 0,
        revenue: 0,
        ppcSpend: 0,
        grossProfit: 0
      };
      
      cumulativeData = cumulativeData.map(row => {
        runningTotals.traffic += row.traffic;
        runningTotals.units += row.units;
        runningTotals.revenue += row.revenue;
        runningTotals.ppcSpend += row.ppcSpend;
        runningTotals.grossProfit += row.grossProfit;
        
        return {
          ...row,
          traffic: runningTotals.traffic,
          units: runningTotals.units,
          revenue: runningTotals.revenue,
          ppcSpend: runningTotals.ppcSpend,
          grossProfit: runningTotals.grossProfit,
          conversionRate: runningTotals.traffic > 0 ? (runningTotals.units / runningTotals.traffic) * 100 : 0
        };
      });
      
      console.log('[OverallSalesHistory] Applied cumulative view mode');
      return cumulativeData.reverse(); // Show most recent first
    }
    
    return filtered.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  }, [rawData, dateRangeKey, viewMode]); // Include viewMode in dependencies

  // Calculate metrics from filtered data
  const metrics = useMemo((): OverallSalesHistoryMetrics | null => {
    if (!filteredData.length) return null;

    const totalRevenue = filteredData.reduce((sum, row) => sum + row.revenue, 0);
    const totalUnits = filteredData.reduce((sum, row) => sum + row.units, 0);
    const totalGrossProfit = filteredData.reduce((sum, row) => sum + row.grossProfit, 0);
    const totalPPCSpend = filteredData.reduce((sum, row) => sum + row.ppcSpend, 0);
    const totalTraffic = filteredData.reduce((sum, row) => sum + row.traffic, 0);
    
    const avgConversionRate = filteredData.length > 0
      ? filteredData.reduce((sum, row) => sum + row.conversionRate, 0) / filteredData.length
      : 0;
    
    const avgTraffic = filteredData.length > 0
      ? totalTraffic / filteredData.length
      : 0;

    const profitMargin = totalRevenue > 0 ? (totalGrossProfit / totalRevenue) * 100 : 0;
    const acos = totalRevenue > 0 ? (totalPPCSpend / totalRevenue) * 100 : 0;

    // Find best and worst performing days
    const sortedByProfit = [...filteredData].sort((a, b) => b.grossProfit - a.grossProfit);
    const bestPerformingDay = sortedByProfit.length > 0 ? {
      date: sortedByProfit[0].date,
      grossProfit: sortedByProfit[0].grossProfit
    } : null;
    
    const worstPerformingDay = sortedByProfit.length > 0 ? {
      date: sortedByProfit[sortedByProfit.length - 1].date,
      grossProfit: sortedByProfit[sortedByProfit.length - 1].grossProfit
    } : null;

    return {
      totalRevenue,
      totalUnits,
      totalGrossProfit,
      totalPPCSpend,
      averageConversionRate: avgConversionRate,
      averageTraffic: avgTraffic,
      totalTraffic,
      profitMargin,
      acos,
      totalDays: filteredData.length,
      bestPerformingDay,
      worstPerformingDay
    };
  }, [filteredData]);

  // Calculate active days count (memoized with stable dependencies)
  const activeDays = useMemo(() => {
    const uniqueDays = new Set(filteredData.filter(row => row.traffic > 0).map(row => row.date.toDateString()));
    return uniqueDays.size;
  }, [filteredData]);

  // Stable date range change handler to prevent unnecessary updates
  const handleDateRangeChange = useCallback((newDateRange: DateRangeValue) => {
    console.log('[OverallSalesHistory] Date range changed:', newDateRange);
    setDateRange(newDateRange);
  }, []);

  // View mode change handler
  const handleViewModeChange = useCallback((newViewMode: 'daily' | 'cumulative') => {
    console.log('[OverallSalesHistory] View mode changed:', newViewMode);
    setViewMode(newViewMode);
  }, []);

  // Expose view mode change to NavigationMenu via window
  useEffect(() => {
    (window as any).setOverallViewMode = handleViewModeChange;
    return () => {
      delete (window as any).setOverallViewMode;
    };
  }, [handleViewModeChange]);

  // Company selection state
  if (!selectedCompany) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-800">
            <FaChartLine className="h-6 w-6 text-gray-400" />
          </div>
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">No Company Selected</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Please select a company from the sidebar to view overall sales history.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Card className="border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
          <CardContent className="py-12">
            <div className="flex flex-col items-center justify-center text-center space-y-4">
              <AlertCircle className="h-12 w-12 text-red-400 dark:text-red-600" />
              <div className="space-y-2">
                <h3 className="text-lg font-semibold text-red-900 dark:text-red-100">
                  Error Loading Data
                </h3>
                <p className="text-sm text-red-700 dark:text-red-300 max-w-md">
                  {error}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="relative h-full overflow-auto">
      {/* Overlay loader for both initial and in-page loading */}
      {loading && (
        <LoadingIndicator
          message={initialLoad ? "Loading Overall Sales Data" : "Updating Overall Data"}
          progress={loadingProgress}
          overlay={true}
          size="md"
        />
      )}
      
      {/* Main content with conditional dimming */}
      <div className={`p-4 md:p-6 space-y-4 md:space-y-6 max-w-full transition-opacity duration-200 ${loading ? 'opacity-50' : 'opacity-100'}`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-gray-100 truncate">
              Overall Sales History
            </h1>
            <p className="text-xs md:text-sm text-gray-500 dark:text-gray-400 mt-1 truncate">
              {companyName || selectedCompany} - {viewMode === 'daily' ? 'Daily Breakdown' : 'Cumulative Totals'}
              {filteredData.length > 0 && ` (${filteredData.length} records)`}
            </p>
          </div>
          <div className="flex-shrink-0">
            <DateRangeSelector
              value={dateRange}
              onChange={handleDateRangeChange}
            />
          </div>
        </div>

        <div className="space-y-6 md:space-y-8 w-full overflow-hidden">
          {/* Quick Insights - Moved to top */}
          <section className="w-full">
            <OverallQuickInsights
              data={filteredData}
              metrics={metrics}
              loading={loading}
            />
          </section>

          {/* Overall Leaderboard Section */}
          <section className="w-full overflow-hidden">
            <OverallPerformanceCharts
              data={filteredData}
              loading={loading}
              performanceFilter={performanceFilter}
              filterType={filterType}
            />
          </section>
        </div>
      </div>
    </div>
  );
};

export default OverallSalesHistory;